import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { Calculator, DollarSign, TrendingUp, BarChart3, Info, Lightbulb } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ValuationCalculator = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [method, setMethod] = useState("revenue");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  // Revenue Multiple inputs
  const [annualRevenue, setAnnualRevenue] = useState(500000);
  const [industryMultiple, setIndustryMultiple] = useState(5);

  // Berkus inputs
  const [soundIdea, setSoundIdea] = useState(0.5);
  const [prototype, setPrototype] = useState(0.5);
  const [qualityTeam, setQualityTeam] = useState(0.5);
  const [strategicRel, setStrategicRel] = useState(0.5);
  const [productRollout, setProductRollout] = useState(0.5);

  // Scorecard inputs
  const [baseValuation, setBaseValuation] = useState(2000000);
  const [teamScore, setTeamScore] = useState(1.0);
  const [opportunityScore, setOpportunityScore] = useState(1.0);
  const [productScore, setProductScore] = useState(1.0);
  const [competitiveScore, setCompetitiveScore] = useState(1.0);

  // DCF inputs
  const [cashFlows, setCashFlows] = useState([100000, 200000, 400000, 700000, 1000000]);
  const [discountRate, setDiscountRate] = useState(25);
  const [terminalGrowth, setTerminalGrowth] = useState(3);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const calculateValuation = async () => {
    setLoading(true);
    try {
      const payload = {
        method,
        annual_revenue: method === 'revenue' ? annualRevenue : null,
        industry_multiple: method === 'revenue' ? industryMultiple : null,
        use_berkus: method === 'berkus',
        sound_idea: soundIdea,
        prototype: prototype,
        quality_team: qualityTeam,
        strategic_relationships: strategicRel,
        product_rollout: productRollout,
        use_scorecard: method === 'scorecard',
        base_valuation: baseValuation,
        team_score: teamScore,
        opportunity_score: opportunityScore,
        product_score: productScore,
        competitive_score: competitiveScore,
        projected_cash_flows: method === 'dcf' ? cashFlows : null,
        discount_rate: discountRate / 100,
        terminal_growth: terminalGrowth / 100
      };

      const res = await axios.post(`${API}/tools/valuation-calculator`, payload, { headers });
      setResults(res.data);
      toast({ title: "Valuation Calculated", description: "Results ready!" });
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "Calculation failed", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Header />

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 flex items-center justify-center gap-3">
              <Calculator className="h-10 w-10 text-blue-600" />
              Startup Valuation Calculator
            </h1>
            <p className="text-gray-600 mt-2">Calculate your startup's value using multiple methodologies</p>
          </div>

          <Tabs value={method} onValueChange={setMethod}>
            <TabsList className="grid grid-cols-4 w-full mb-8">
              <TabsTrigger value="revenue">Revenue Multiple</TabsTrigger>
              <TabsTrigger value="berkus">Berkus Method</TabsTrigger>
              <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
              <TabsTrigger value="dcf">DCF</TabsTrigger>
            </TabsList>

            {/* Revenue Multiple */}
            <TabsContent value="revenue">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Revenue Multiple Method
                  </CardTitle>
                  <CardDescription>
                    Best for revenue-generating startups. Valuation = Annual Revenue × Industry Multiple
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Annual Revenue ($)</Label>
                    <Input
                      type="number"
                      value={annualRevenue}
                      onChange={(e) => setAnnualRevenue(Number(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Industry Multiple</Label>
                      <span className="font-bold text-blue-600">{industryMultiple}x</span>
                    </div>
                    <Slider
                      value={[industryMultiple]}
                      onValueChange={([v]) => setIndustryMultiple(v)}
                      min={1}
                      max={20}
                      step={0.5}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1x (Traditional)</span>
                      <span>10x (SaaS Avg)</span>
                      <span>20x (High Growth)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Berkus Method */}
            <TabsContent value="berkus">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                    Berkus Method
                  </CardTitle>
                  <CardDescription>
                    Best for pre-revenue startups. Each factor adds up to $500K (max $2.5M valuation)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {[
                    { label: "Sound Idea / Business Model", value: soundIdea, setter: setSoundIdea },
                    { label: "Prototype / MVP Built", value: prototype, setter: setPrototype },
                    { label: "Quality Management Team", value: qualityTeam, setter: setQualityTeam },
                    { label: "Strategic Relationships", value: strategicRel, setter: setStrategicRel },
                    { label: "Product Rollout / Sales", value: productRollout, setter: setProductRollout }
                  ].map((factor, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between mb-2">
                        <Label>{factor.label}</Label>
                        <span className="font-bold text-purple-600">${(factor.value * 500000).toLocaleString()}</span>
                      </div>
                      <Slider
                        value={[factor.value]}
                        onValueChange={([v]) => factor.setter(v)}
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Scorecard Method */}
            <TabsContent value="scorecard">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-amber-600" />
                    Scorecard Method
                  </CardTitle>
                  <CardDescription>
                    Compare against average pre-money valuation in your region. Score each factor vs. average (1.0 = average)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Regional Average Pre-Money ($)</Label>
                    <Input
                      type="number"
                      value={baseValuation}
                      onChange={(e) => setBaseValuation(Number(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  {[
                    { label: "Team (30%)", value: teamScore, setter: setTeamScore },
                    { label: "Opportunity Size (25%)", value: opportunityScore, setter: setOpportunityScore },
                    { label: "Product/Technology (15%)", value: productScore, setter: setProductScore },
                    { label: "Competitive Environment (10%)", value: competitiveScore, setter: setCompetitiveScore }
                  ].map((factor, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between mb-2">
                        <Label>{factor.label}</Label>
                        <span className="font-bold text-amber-600">{(factor.value * 100).toFixed(0)}%</span>
                      </div>
                      <Slider
                        value={[factor.value]}
                        onValueChange={([v]) => factor.setter(v)}
                        min={0.5}
                        max={1.5}
                        step={0.05}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Below Avg</span>
                        <span>Average</span>
                        <span>Above Avg</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* DCF Method */}
            <TabsContent value="dcf">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-blue-600" />
                    Discounted Cash Flow (DCF)
                  </CardTitle>
                  <CardDescription>
                    Best for startups with predictable cash flows. Enter 5-year projections.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-5 gap-4">
                    {cashFlows.map((cf, idx) => (
                      <div key={idx}>
                        <Label className="text-sm">Year {idx + 1}</Label>
                        <Input
                          type="number"
                          value={cf}
                          onChange={(e) => {
                            const newCF = [...cashFlows];
                            newCF[idx] = Number(e.target.value);
                            setCashFlows(newCF);
                          }}
                          className="mt-1"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="flex justify-between mb-2">
                        <Label>Discount Rate</Label>
                        <span className="font-bold text-blue-600">{discountRate}%</span>
                      </div>
                      <Slider
                        value={[discountRate]}
                        onValueChange={([v]) => setDiscountRate(v)}
                        min={10}
                        max={50}
                        step={1}
                      />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <Label>Terminal Growth Rate</Label>
                        <span className="font-bold text-blue-600">{terminalGrowth}%</span>
                      </div>
                      <Slider
                        value={[terminalGrowth]}
                        onValueChange={([v]) => setTerminalGrowth(v)}
                        min={1}
                        max={10}
                        step={0.5}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <Button
            onClick={calculateValuation}
            disabled={loading}
            className="w-full mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-6 text-lg"
          >
            {loading ? "Calculating..." : "Calculate Valuation"}
          </Button>

          {/* Results */}
          {results && (
            <Card className="mt-8 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Valuation Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-2">Estimated Valuation</p>
                  <p className="text-5xl font-bold text-blue-600">
                    ${results.average_valuation?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {Object.entries(results.methods || {}).map(([key, data]) => (
                    <Card key={key} className="bg-white">
                      <CardContent className="p-4">
                        <p className="text-sm text-gray-500">{data.method}</p>
                        <p className="text-2xl font-bold">${data.valuation?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
                  <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Recommendation</p>
                    <p className="text-sm text-amber-700">{results.recommendation}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValuationCalculator;
